import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Loader, 
  Calendar,
  MapPin,
  Trophy,
  FileText,
  Image as ImageIcon,
  Star,
  Eye,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Code,
  Type,
  Users
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import CustomSelect from '../components/common/CustomSelect';
import { sanitizeInput, validateAndSanitize } from '../utils/inputSanitization';
import BackToTop from '../components/layout/BackToTop';
import { getDraw } from '../services/drawService';

const AdminResultEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [tournaments, setTournaments] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  
  // Changed: renamed from entrants to drawPlayers for clarity
  const [drawPlayers, setDrawPlayers] = useState([]);
  const [loadingDrawPlayers, setLoadingDrawPlayers] = useState(false);
  const [drawCount, setDrawCount] = useState(0);
  
  const [editorMode, setEditorMode] = useState('visual');
  const [uploading, setUploading] = useState(false);
  const visualEditorRef = useRef(null);
  const isInitialMount = useRef(true);

  // Form data
  const [formData, setFormData] = useState({
    tournament_id: '',
    tournament_name: '',
    tournament_date: '',
    category: '',
    venue: '',
    format: '',
    winner_name: '',
    winner_score: '',
    winner_club: '',
    report_title: '',
    report_content: '',
    report_author: '',
    featured_image_url: '',
    featured_image_caption: '',
    status: 'draft',
    is_featured: 0,
    featured_order: 0,
    meta_description: '',
    slug: ''
  });

  // Categories
  const categories = [
    { value: '', label: 'Select Category' },
    { value: "Men's Open", label: "Men's Open" },
    { value: "Men's Mid-Am", label: "Men's Mid-Am" },
    { value: "Men's Senior", label: "Men's Senior" },
    { value: 'Junior Boys', label: 'Junior Boys' },
    { value: "Ladies Open", label: "Ladies Open" },
    { value: "Ladies Mid-Am", label: "Ladies Mid-Am" },
    { value: "Ladies Senior", label: "Ladies Senior" },
    { value: 'Junior Girls', label: 'Junior Girls' },
    { value: 'Mixed', label: 'Mixed' }
  ];

  // Map tournament category format to result category format
  const mapTournamentCategory = (tournamentCategory) => {
    const categoryMap = {
      'Mens_Open': "Men's Open",
      'Mens_Mid_Am': "Men's Mid-Am",
      'Mens_Senior': "Men's Senior",
      'Mens_Super_Senior': "Men's Super Senior",
      'Junior_Boys': 'Junior Boys',
      'Ladies_Open': "Ladies Open",
      'Ladies_Mid_Am': "Ladies Mid-Am",
      'Ladies_Senior': "Ladies Senior",
      'Ladies_Super_Senior': "Ladies Super Senior",
      'Junior_Girls': 'Junior Girls',
      'Mixed': 'Mixed',
      'Country_Districts': 'Country Districts'
    };
    
    return categoryMap[tournamentCategory] || tournamentCategory;
  };

  // Status options
  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' }
  ];

  // Load result data if editing
  useEffect(() => {
    loadTournaments();
    if (isEditMode) {
      loadResult();
    }
  }, [id]);

  // Set initial content in visual editor
  useEffect(() => {
    if (visualEditorRef.current && formData.report_content && isInitialMount.current) {
      visualEditorRef.current.innerHTML = formData.report_content;
      isInitialMount.current = false;
    }
  }, [formData.report_content]);

  // Load tournaments from API
  const loadTournaments = async () => {
    try {
      setLoadingTournaments(true);
      const response = await fetch('https://api.fsgu.co.za/get-tournaments.php?status=all');
      const data = await response.json();
      
      if (data.success && data.data) {
        // Format tournaments for dropdown
        const tournamentOptions = data.data.map(t => ({
          value: t.id,
          label: `${t.tournament_name} (${new Date(t.start_date).toLocaleDateString('en-ZA')})`,
          data: t
        }));
        setTournaments([
          { value: '', label: 'Select Tournament (Optional)' },
          ...tournamentOptions
        ]);
      }
    } catch (error) {
      console.error('Error loading tournaments:', error);
      setTournaments([{ value: '', label: 'Select Tournament (Optional)' }]);
    } finally {
      setLoadingTournaments(false);
    }
  };

  // UPDATED: Load draw players for selected tournament (instead of registrations)
  const loadDrawPlayers = async (tournamentId) => {
    if (!tournamentId) {
      setDrawPlayers([{ value: '', label: 'Select Winner (Optional)' }]);
      setDrawCount(0);
      return;
    }

    try {
      setLoadingDrawPlayers(true);
      const response = await getDraw(tournamentId);
      
      if (response.data && response.data.length > 0) {
        // Format draw players for dropdown
        // Sort alphabetically by player name for easier selection
        const sortedPlayers = [...response.data].sort((a, b) => 
          a.player_name.localeCompare(b.player_name)
        );
        
        const playerOptions = sortedPlayers.map(p => ({
          value: p.id,
          label: `${p.player_name}${p.club ? ` (${p.club})` : ''}${p.handicap !== null ? ` - HCP: ${p.handicap}` : ''}`,
          data: p
        }));
        
        setDrawPlayers([
          { value: '', label: 'Select Winner (Optional)' },
          ...playerOptions
        ]);
        setDrawCount(response.data.length);
      } else {
        setDrawPlayers([{ value: '', label: 'No players in draw' }]);
        setDrawCount(0);
      }
    } catch (error) {
      console.error('Error loading draw players:', error);
      setDrawPlayers([{ value: '', label: 'Failed to load draw' }]);
      setDrawCount(0);
    } finally {
      setLoadingDrawPlayers(false);
    }
  };

  const loadResult = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://api.fsgu.co.za/get-result.php?id=${id}`);
      const data = await response.json();
      
      if (data.success && data.data) {
        setFormData({
          ...data.data,
          is_featured: data.data.is_featured ? 1 : 0
        });
        
        // If result has a tournament_id, load the draw for that tournament
        if (data.data.tournament_id) {
          loadDrawPlayers(data.data.tournament_id);
        }
      } else {
        alert('Result not found');
        navigate('/admin/results');
      }
    } catch (error) {
      console.error('Error loading result:', error);
      alert('Failed to load result');
    } finally {
      setLoading(false);
    }
  };

  // Handle visual editor changes
  const handleVisualEditorChange = () => {
    if (visualEditorRef.current) {
      const htmlContent = visualEditorRef.current.innerHTML;
      setFormData(prev => ({ ...prev, report_content: htmlContent }));
    }
  };

  // Switch between visual and code mode
  const switchEditorMode = (mode) => {
    if (mode === 'code' && editorMode === 'visual') {
      if (visualEditorRef.current) {
        setFormData(prev => ({ ...prev, report_content: visualEditorRef.current.innerHTML }));
      }
    } else if (mode === 'visual' && editorMode === 'code') {
      setTimeout(() => {
        if (visualEditorRef.current) {
          visualEditorRef.current.innerHTML = formData.report_content;
        }
      }, 0);
    }
    setEditorMode(mode);
  };

  // Execute formatting command
  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    visualEditorRef.current?.focus();
    handleVisualEditorChange();
  };

  const formatBold = () => executeCommand('bold');
  const formatItalic = () => executeCommand('italic');
  const formatUnderline = () => executeCommand('underline');
  const formatH1 = () => executeCommand('formatBlock', '<h1>');
  const formatH2 = () => executeCommand('formatBlock', '<h2>');
  const formatParagraph = () => executeCommand('formatBlock', '<p>');
  const formatUL = () => executeCommand('insertUnorderedList');
  const formatOL = () => executeCommand('insertOrderedList');
  
  const formatLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      executeCommand('createLink', url);
    }
  };
  
  const formatImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      executeCommand('insertImage', url);
    }
  };

  // Compress image for upload
  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          const maxWidth = 1200;
          const maxHeight = 1200;
          
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/jpeg', 0.85);
        };
      };
    });
  };

  // Handle featured image upload
  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      
      const compressedBlob = await compressImage(file);
      const formDataUpload = new FormData();
      formDataUpload.append('image', compressedBlob, file.name);
      formDataUpload.append('folder', 'results');
      
      const response = await fetch('https://api.fsgu.co.za/upload-image.php', {
        method: 'POST',
        body: formDataUpload
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          featured_image_url: data.url
        }));
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Generate slug from tournament name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Handle tournament selection
  const handleTournamentSelect = (e) => {
    const tournamentId = e.target.value;
    
    if (!tournamentId) {
      // Clear tournament fields but keep other data
      setFormData(prev => ({
        ...prev,
        tournament_id: '',
        tournament_name: '',
        tournament_date: '',
        category: '',
        venue: '',
        format: '',
        slug: ''
      }));
      setDrawPlayers([{ value: '', label: 'Select Winner (Optional)' }]);
      setDrawCount(0);
      return;
    }

    // Find selected tournament
    const tournament = tournaments.find(t => t.value === parseInt(tournamentId))?.data;
    
    if (tournament) {
      setFormData(prev => ({
        ...prev,
        tournament_id: tournament.id,
        tournament_name: tournament.tournament_name,
        tournament_date: tournament.start_date,
        category: mapTournamentCategory(tournament.category),
        venue: tournament.venue_name || tournament.venue_city || '',
        format: tournament.format || '',
        slug: generateSlug(tournament.tournament_name)
      }));

      // Load draw players for this tournament
      loadDrawPlayers(tournament.id);
    }
  };

  // UPDATED: Handle winner selection from draw (instead of entrants)
  const handleDrawPlayerSelect = (e) => {
    const playerId = e.target.value;
    
    if (!playerId) {
      // Clear winner fields
      setFormData(prev => ({
        ...prev,
        winner_name: '',
        winner_club: ''
      }));
      return;
    }

    // Find selected player from draw
    const player = drawPlayers.find(p => p.value === parseInt(playerId))?.data;
    
    if (player) {
      setFormData(prev => ({
        ...prev,
        winner_name: player.player_name,
        winner_club: player.club || ''
      }));
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let sanitized = value;
    
    // Apply sanitization based on field type
    if (type !== 'checkbox') {
      if (name === 'report_content' && editorMode === 'code') {
        sanitized = value;
      } else if (name === 'winner_score') {
        sanitized = value; // Allow +/- scores
      } else {
        sanitized = sanitizeInput(value, 'text');
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : sanitized
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Auto-generate slug when title changes (for new results)
    if (name === 'tournament_name' && !isEditMode) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.tournament_name.trim()) {
      newErrors.tournament_name = 'Tournament name is required';
    }
    if (!formData.tournament_date) {
      newErrors.tournament_date = 'Tournament date is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.venue.trim()) {
      newErrors.venue = 'Venue is required';
    }
    if (!formData.winner_name.trim()) {
      newErrors.winner_name = 'Winner name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Capture visual editor content
    let contentToSave = formData.report_content;
    if (editorMode === 'visual' && visualEditorRef.current) {
      contentToSave = visualEditorRef.current.innerHTML;
    }

    // Update formData with captured content for validation
    const dataToSubmit = {
      ...formData,
      report_content: contentToSave
    };

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      const endpoint = isEditMode 
        ? 'https://api.fsgu.co.za/update-result.php'
        : 'https://api.fsgu.co.za/create-result.php';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSubmit)
      });

      const data = await response.json();

      if (data.success) {
        alert(isEditMode ? 'Result updated successfully!' : 'Result created successfully!');
        navigate('/admin/results');
      } else {
        throw new Error(data.message || 'Failed to save result');
      }
    } catch (error) {
      console.error('Error saving result:', error);
      alert('Failed to save result: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="container-custom py-6">
          <div className="flex items-center justify-center h-64">
            <Loader className={`w-8 h-8 animate-spin ${
              isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
            }`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable]:focus[data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #d1d5db;
        }
      `}</style>
      
      <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      <div className="container-custom py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/results"
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'hover:bg-fsgu-gray-800 text-fsgu-silver-300'
                  : 'hover:bg-fsgu-silver-100 text-fsgu-silver-600'
              }`}
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className={`text-3xl font-bold font-cursive ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                {isEditMode ? 'Edit Result' : 'Add New Result'}
              </h1>
              <p className={`text-sm mt-1 ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                {isEditMode ? 'Update tournament result details' : 'Create a new tournament result'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tournament Selector */}
          {!isEditMode && (
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${
                  isDarkMode ? 'bg-fsgu-blue-500/20' : 'bg-fsgu-blue-500/10'
                }`}>
                  <Trophy className={`w-5 h-5 ${
                    isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-500'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    Link to Existing Tournament
                  </h3>
                  <p className={`text-sm mb-4 ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    Select an existing tournament to auto-fill details, or leave blank to enter manually.
                  </p>
                  
                  {loadingTournaments ? (
                    <div className="flex items-center space-x-2">
                      <Loader className="w-4 h-4 animate-spin text-fsgu-green-500" />
                      <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                        Loading tournaments...
                      </span>
                    </div>
                  ) : (
                    <CustomSelect
                      value={formData.tournament_id}
                      onChange={handleTournamentSelect}
                      options={tournaments}
                      placeholder="Select Tournament (Optional)"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tournament Details */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <Trophy className="w-5 h-5 mr-2" />
              Tournament Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Tournament Name */}
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Tournament Name *
                </label>
                <input
                  type="text"
                  name="tournament_name"
                  value={formData.tournament_name}
                  onChange={handleChange}
                  className={`input ${errors.tournament_name ? 'border-red-500' : ''}`}
                  placeholder="e.g., Free State Open Championship 2024"
                />
                {errors.tournament_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.tournament_name}</p>
                )}
              </div>

              {/* Tournament Date */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Tournament Date *
                </label>
                <input
                  type="date"
                  name="tournament_date"
                  value={formData.tournament_date}
                  onChange={handleChange}
                  className={`input ${errors.tournament_date ? 'border-red-500' : ''}`}
                />
                {errors.tournament_date && (
                  <p className="text-red-500 text-sm mt-1">{errors.tournament_date}</p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Category *
                </label>
                <CustomSelect
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  options={categories}
                  className={errors.category ? 'border-red-500' : ''}
                />
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                )}
              </div>

              {/* Venue */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Venue *
                </label>
                <input
                  type="text"
                  name="venue"
                  value={formData.venue}
                  onChange={handleChange}
                  className={`input ${errors.venue ? 'border-red-500' : ''}`}
                  placeholder="e.g., Bloemfontein Golf Club"
                />
                {errors.venue && (
                  <p className="text-red-500 text-sm mt-1">{errors.venue}</p>
                )}
              </div>

              {/* Format */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Format
                </label>
                <input
                  type="text"
                  name="format"
                  value={formData.format}
                  onChange={handleChange}
                  className="input"
                  placeholder="e.g., 72 Holes Stroke Play"
                />
              </div>
            </div>
          </div>

          {/* Winner Details */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <Trophy className="w-5 h-5 mr-2" />
              Winner Details
            </h2>

            {/* UPDATED: Draw Player Selector - Only show if tournament is selected */}
            {formData.tournament_id && (
              <div className="mb-6 p-4 rounded-lg bg-fsgu-green-500/10 border border-fsgu-green-500/20">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  <Users className="w-4 h-4 inline mr-2" />
                  Select Winner from Draw
                </label>
                <p className={`text-xs mb-3 ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                }`}>
                  Choose from the tournament draw to auto-fill winner details
                  {drawCount > 0 && (
                    <span className="ml-2 font-medium text-fsgu-green-500">
                      ({drawCount} players in draw)
                    </span>
                  )}
                </p>
                
                {loadingDrawPlayers ? (
                  <div className="flex items-center space-x-2">
                    <Loader className="w-4 h-4 animate-spin text-fsgu-green-500" />
                    <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                      Loading draw...
                    </span>
                  </div>
                ) : (
                  <CustomSelect
                    onChange={handleDrawPlayerSelect}
                    options={drawPlayers}
                    placeholder="Select Winner (Optional)"
                  />
                )}
                
                {drawCount === 0 && !loadingDrawPlayers && (
                  <p className={`text-xs mt-2 ${
                    isDarkMode ? 'text-amber-400' : 'text-amber-600'
                  }`}>
                    No draw available for this tournament. You can still enter winner details manually.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Winner Name */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Winner Name *
                </label>
                <input
                  type="text"
                  name="winner_name"
                  value={formData.winner_name}
                  onChange={handleChange}
                  className={`input ${errors.winner_name ? 'border-red-500' : ''}`}
                  placeholder="e.g., John Smith"
                />
                {errors.winner_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.winner_name}</p>
                )}
              </div>

              {/* Winner Score */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Winner Score
                </label>
                <input
                  type="text"
                  name="winner_score"
                  value={formData.winner_score}
                  onChange={handleChange}
                  className="input"
                  placeholder="e.g., -5 (140)"
                />
              </div>

              {/* Winner Club */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Winner Club
                </label>
                <input
                  type="text"
                  name="winner_club"
                  value={formData.winner_club}
                  onChange={handleChange}
                  className="input"
                  placeholder="e.g., Bloemfontein Golf Club"
                />
              </div>
            </div>
          </div>

          {/* Report Content */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <FileText className="w-5 h-5 mr-2" />
              Report Content
            </h2>

            <div className="space-y-4">
              {/* Report Title */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Report Title
                </label>
                <input
                  type="text"
                  name="report_title"
                  value={formData.report_title}
                  onChange={handleChange}
                  className="input"
                  placeholder="e.g., Smith Claims Victory at Free State Open"
                />
              </div>

              {/* Editor Mode Toggle */}
              <div className="flex items-center justify-between">
                <label className={`block text-sm font-medium ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Report Content
                </label>
                <div className="flex rounded-lg overflow-hidden border">
                  <button
                    type="button"
                    onClick={() => switchEditorMode('visual')}
                    className={`px-3 py-1 text-sm ${
                      editorMode === 'visual'
                        ? 'bg-fsgu-green-500 text-white'
                        : isDarkMode
                          ? 'bg-fsgu-gray-700 text-fsgu-silver-300'
                          : 'bg-fsgu-silver-100 text-fsgu-silver-700'
                    }`}
                  >
                    <Eye className="w-4 h-4 inline mr-1" />
                    Visual
                  </button>
                  <button
                    type="button"
                    onClick={() => switchEditorMode('code')}
                    className={`px-3 py-1 text-sm ${
                      editorMode === 'code'
                        ? 'bg-fsgu-green-500 text-white'
                        : isDarkMode
                          ? 'bg-fsgu-gray-700 text-fsgu-silver-300'
                          : 'bg-fsgu-silver-100 text-fsgu-silver-700'
                    }`}
                  >
                    <Code className="w-4 h-4 inline mr-1" />
                    HTML
                  </button>
                </div>
              </div>

              {/* Visual Editor */}
              {editorMode === 'visual' && (
                <>
                  {/* Toolbar */}
                  <div className={`flex flex-wrap gap-1 p-2 rounded-t-lg border ${
                    isDarkMode
                      ? 'bg-fsgu-gray-900 border-fsgu-gray-700'
                      : 'bg-fsgu-silver-50 border-fsgu-silver-200'
                  }`}>
                    <button type="button" onClick={formatBold} className="editor-btn" title="Bold">
                      <Bold className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={formatItalic} className="editor-btn" title="Italic">
                      <Italic className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={formatUnderline} className="editor-btn" title="Underline">
                      <Underline className="w-4 h-4" />
                    </button>
                    <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-fsgu-gray-700' : 'bg-fsgu-silver-300'}`} />
                    <button type="button" onClick={formatH1} className="editor-btn" title="Heading 1">
                      <Heading1 className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={formatH2} className="editor-btn" title="Heading 2">
                      <Heading2 className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={formatParagraph} className="editor-btn" title="Paragraph">
                      <Type className="w-4 h-4" />
                    </button>
                    <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-fsgu-gray-700' : 'bg-fsgu-silver-300'}`} />
                    <button type="button" onClick={formatUL} className="editor-btn" title="Bullet List">
                      <List className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={formatOL} className="editor-btn" title="Numbered List">
                      <ListOrdered className="w-4 h-4" />
                    </button>
                    <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-fsgu-gray-700' : 'bg-fsgu-silver-300'}`} />
                    <button type="button" onClick={formatLink} className="editor-btn" title="Insert Link">
                      <LinkIcon className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={formatImage} className="editor-btn" title="Insert Image">
                      <ImageIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Editable Area */}
                  <div
                    ref={visualEditorRef}
                    contentEditable
                    onInput={handleVisualEditorChange}
                    data-placeholder="Write your tournament report here..."
                    className={`min-h-[300px] p-4 rounded-b-lg border border-t-0 focus:outline-none focus:ring-2 focus:ring-fsgu-green-500 ${
                      isDarkMode
                        ? 'bg-fsgu-gray-900 border-fsgu-gray-700 text-fsgu-silver-200'
                        : 'bg-white border-fsgu-silver-200 text-fsgu-silver-900'
                    } prose max-w-none`}
                  />
                </>
              )}

              {/* Code Editor */}
              {editorMode === 'code' && (
                <textarea
                  name="report_content"
                  value={formData.report_content}
                  onChange={handleChange}
                  rows={15}
                  className={`input font-mono text-sm ${
                    isDarkMode ? 'bg-fsgu-gray-900' : 'bg-white'
                  }`}
                  placeholder="<p>Enter HTML content here...</p>"
                />
              )}

              {/* Report Author */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Report Author
                </label>
                <input
                  type="text"
                  name="report_author"
                  value={formData.report_author}
                  onChange={handleChange}
                  className="input"
                  placeholder="e.g., FSGU Media Team"
                />
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <ImageIcon className="w-5 h-5 mr-2" />
              Featured Image
            </h2>

            <div className="space-y-4">
              {/* Image Preview */}
              {formData.featured_image_url && (
                <div className="relative w-full max-w-md">
                  <img
                    src={formData.featured_image_url}
                    alt="Featured"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, featured_image_url: '' }))}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Upload */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  {formData.featured_image_url ? 'Replace Image' : 'Upload Image'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFeaturedImageUpload}
                  disabled={uploading}
                  className="input"
                />
                {uploading && (
                  <div className="flex items-center mt-2 text-fsgu-green-500">
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Uploading...
                  </div>
                )}
              </div>

              {/* Image URL */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Or Enter Image URL
                </label>
                <input
                  type="text"
                  name="featured_image_url"
                  value={formData.featured_image_url}
                  onChange={handleChange}
                  className="input"
                  placeholder="https://..."
                />
              </div>

              {/* Image Caption */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Image Caption
                </label>
                <input
                  type="text"
                  name="featured_image_caption"
                  value={formData.featured_image_caption}
                  onChange={handleChange}
                  className="input"
                  placeholder="e.g., John Smith with the winner's trophy"
                />
              </div>
            </div>
          </div>

          {/* SEO & Publishing */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <Star className="w-5 h-5 mr-2" />
              Publishing Settings
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Status */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Status
                </label>
                <CustomSelect
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  options={statusOptions}
                />
              </div>

              {/* Slug */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  URL Slug
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className="input"
                  placeholder="auto-generated-from-title"
                />
              </div>

              {/* Meta Description */}
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Meta Description
                </label>
                <textarea
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                  rows={2}
                  className="input"
                  placeholder="Brief description for search engines..."
                />
              </div>

              {/* Featured */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured === 1}
                  onChange={handleChange}
                  className="w-4 h-4 text-fsgu-green-500 rounded"
                />
                <label className={`text-sm ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Feature this result
                </label>
              </div>

              {/* Featured Order */}
              {formData.is_featured === 1 && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Featured Order
                  </label>
                  <input
                    type="number"
                    name="featured_order"
                    value={formData.featured_order}
                    onChange={handleChange}
                    className="input"
                    min="0"
                    placeholder="0"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4">
            <Link
              to="/admin/results"
              className="btn btn-secondary"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary inline-flex items-center"
            >
              {saving ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {isEditMode ? 'Update Result' : 'Create Result'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    <BackToTop /> 
    </>
  );
};

export default AdminResultEditor;